import { readGroupSettings, useUpdateGroup } from '@hobbytools/data';
import { friendlyError } from '@hobbytools/platform';
import { Banner, Button, Card, Switch, TextField } from '@hobbytools/ui';
import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router';
import { useGroupContext } from './GroupLayout';

/** /groups/:id/settings — admins and the owner. */
export function GroupSettings() {
  const { group, canManage } = useGroupContext();
  const update = useUpdateGroup(group.id);
  const settings = readGroupSettings(group);
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to={`/groups/${group.id}`} replace />;

  const run = async (input: Parameters<typeof update.mutateAsync>[0]) => {
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync(input);
      setSaved(true);
    } catch (err) {
      setError(friendlyError(err));
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run({ name: name.trim(), description: description.trim() });
  };

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {saved ? <Banner tone="success">Saved.</Banner> : null}

      <Card>
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <h2 className="heading-display text-xl">Details</h2>
          <TextField label="Group name" isRequired maxLength={80} value={name} onChange={setName} />
          <TextField
            label="Description"
            description="Shown under the group name. Where and when you play is a good start."
            maxLength={2000}
            value={description}
            onChange={setDescription}
          />
          <Button type="submit" className="self-start" isDisabled={update.isPending || !name.trim()}>
            Save details
          </Button>
        </form>
      </Card>

      <Card className="flex flex-col gap-4">
        <h2 className="heading-display text-xl">Joining</h2>
        <Switch
          label="Approve new members automatically"
          description="When off, people who use an invite wait until an admin approves them."
          isSelected={settings.autoApproveMembers}
          isDisabled={update.isPending}
          onChange={(autoApproveMembers) => run({ settings: { ...settings, autoApproveMembers } })}
        />
        <Switch
          label="Allow joining with invite codes"
          description="When off, existing invite codes stop working until you turn this back on."
          isSelected={settings.joinViaCode}
          isDisabled={update.isPending}
          onChange={(joinViaCode) => run({ settings: { ...settings, joinViaCode } })}
        />
      </Card>
    </div>
  );
}
